import { useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { isImeComposing } from '@utils/ime';

/**
 * How a form dialog is dismissed without its Cancel button — a backdrop click
 * or Escape — and the guard that keeps typed work from vanishing on one.
 *
 * - A backdrop click closes only when the press STARTED on the backdrop too: a
 *   text-selection drag that begins in a field and ends outside the dialog is
 *   not a click on the backdrop, though the browser reports it as one.
 * - Escape closes unless something inside already handled it (a picker that
 *   closes itself marks it `defaultPrevented`) or an IME is composing — that
 *   Escape cancels the conversion, not the form.
 * - Either asks first when the form is `dirty`: `confirming` turns true and the
 *   host renders its discard confirmation, answered by `discard`/`keepEditing`.
 */
export function useDismissGuard(dirty: boolean, close: () => void) {
  const [confirming, setConfirming] = useState(false);
  const pressedOnBackdrop = useRef(false);

  const requestClose = () => (dirty ? setConfirming(true) : close());

  return {
    confirming,
    discard: () => {
      setConfirming(false);
      close();
    },
    keepEditing: () => setConfirming(false),
    backdropProps: {
      onMouseDown: (e: MouseEvent) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      },
      onClick: (e: MouseEvent) => {
        const fromBackdrop = pressedOnBackdrop.current && e.target === e.currentTarget;
        pressedOnBackdrop.current = false;
        if (fromBackdrop) requestClose();
      },
      onKeyDown: (e: KeyboardEvent) => {
        if (e.key !== 'Escape' || e.defaultPrevented || isImeComposing(e)) return;
        e.preventDefault();
        requestClose();
      },
    },
  };
}
